import React from 'react';
import PropTypes from 'prop-types';
import useComponentDidMount from 'Hooks/useComponentDidMount';
import fr from 'facet-rest';

const LogsContext = React.createContext(undefined);

// Actions
const INITIALIZE_STARTED = 'initialize-started';
const INITIALIZE_SUCCEEDED = 'initialize-succeeded';
const INITIALIZE_FAILED = 'initialize-failed';

const LOGS_REFRESH_STARTED = 'logs-refresh-started';
const LOGS_REFRESH_SUCCEEDED = 'logs-refresh-succeeded';
const LOGS_REFRESH_FAILED = 'logs-refresh-failed';

const init = () => {
  return {
    isInitializing: true,
    initializingError: null,
    logsCollection: null,
    isLogsRefreshing: false,
    logsRefreshError: null,
  };
};

// Reducer
const reducer = (state, action) => {
  switch (action.type) {
    case INITIALIZE_STARTED: {
      return {
        ...state,
        isInitializing: true,
        initializingError: null,
      };
    }
    case INITIALIZE_SUCCEEDED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: null,
        logsCollection: action.logsCollection,
      };
    }
    case INITIALIZE_FAILED: {
      return {
        ...state,
        isInitializing: false,
        initializingError: action.initializationError,
      };
    }
    case LOGS_REFRESH_STARTED: {
      return {
        ...state,
        isLogsRefreshing: true,
        logsRefreshError: null,
      };
    }
    case LOGS_REFRESH_SUCCEEDED: {
      return {
        ...state,
        isLogsRefreshing: false,
        logsRefreshError: null,
        logsCollection: action.logsCollection,
      };
    }
    case LOGS_REFRESH_FAILED: {
      return {
        ...state,
        isLogsRefreshing: false,
        logsRefreshError: action.logsRefreshError,
      };
    }
    default: {
      throw new Error(`Unhandled LogsContext action type: ${action.type}`);
    }
  }
};

const LogsProvider = ({
  tabResource,
  children,
}) => {
  const [state, dispatch] = React.useReducer(reducer, null, init);

  const refreshLogsCollection = React.useCallback(async () => {
    try {
      dispatch({ type: LOGS_REFRESH_STARTED });

      const coll = await tabResource.relationship(fr.relationships.logs.name).link('related').get();

      dispatch({ type: LOGS_REFRESH_SUCCEEDED, logsCollection: coll });
    } catch (err) {
      dispatch({ type: LOGS_REFRESH_FAILED, logsRefreshError: err });
    }
  }, [state]);

  const loadLogsCollectionNext = async () => {
    try {
      const { logsCollection } = state;
      dispatch({ type: LOGS_REFRESH_STARTED });
      const resp = await logsCollection.link('next').get();
      dispatch({
        type: LOGS_REFRESH_SUCCEEDED,
        logsCollection: resp,
      });
    } catch (err) {
      dispatch({ type: LOGS_REFRESH_FAILED, initializationError: err });
    }
  };

  const loadLogsCollectionPrev = async () => {
    try {
      const { logsCollection } = state;
      dispatch({ type: LOGS_REFRESH_STARTED });
      const resp = await logsCollection.link('prev').get();
      dispatch({
        type: LOGS_REFRESH_SUCCEEDED,
        logsCollection: resp,
      });
    } catch (err) {
      dispatch({ type: LOGS_REFRESH_FAILED, initializationError: err });
    }
  };

  const memoizedValue = React.useMemo(() => {
    return {
      isInitializing: state.isInitializing,
      initializingError: state.initializingError,
      logsCollection: state.logsCollection,
      isLogsRefreshing: state.isLogsRefreshing,
      logsRefreshError: state.logsRefreshError,
      refreshLogsCollection,
      loadLogsCollectionNext,
      loadLogsCollectionPrev,
    };
  }, [
    state,
    refreshLogsCollection,
  ]);

  const loadLogsCollection = async () => {
    try {
      dispatch({ type: INITIALIZE_STARTED });

      const resp = await tabResource.relationship(fr.relationships.logs.name).link('related').get();

      dispatch({
        type: INITIALIZE_SUCCEEDED,
        logsCollection: resp,
      });
    } catch (err) {
      dispatch({ type: INITIALIZE_FAILED, initializationError: err });
    }
  };

  useComponentDidMount(loadLogsCollection);

  return (
    <LogsContext.Provider value={memoizedValue}>
      {children}
    </LogsContext.Provider>
  );
};

// Use Logs State Context
const useLogsContext = () => {
  const context = React.useContext(LogsContext);
  if (context === undefined) {
    throw new Error('useLogsContext must be used within a LogsProvider');
  }
  return context;
};

LogsProvider.propTypes = {
  tabResource: PropTypes.object.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  LogsProvider,
  useLogsContext,
};
